/**
 * Onglet Présentation - Informations générales de l'entreprise
 * Inclut la sélection des lots techniques et zones d'intervention (prestataire libre)
 * FRONT-09: Affichage en lecture seule pour les comptes Accord Cadre
 * FRONT-24: Ajout section "Informations légales" avec données entreprise
 */
import React, { useState, useEffect, useRef } from 'react';
import { useProfile } from '../../../contexts/ProfileContext';
import { useProfilType } from '../hooks/useProfilType';
import type { PresentationData, EntrepriseFormData, AdresseData } from '../../../services/profileService';
import Button from '../../../composants/Button/Button';
import Switch from '../../../composants/Switch/Switch';
import LotsSelectorV2 from '../components/LotsSelectorV2';
import LotsViewByLotTechnique from '../components/LotsViewByLotTechnique';
import './TabPresentation.css';

// Interface pour les suggestions IGN (autocomplétion adresse)
interface IgnSuggestion {
  label: string;
  houseNumber?: string;
  street?: string;
  postcode?: string;
  city?: string;
}

// Formes juridiques disponibles
const FORMES_JURIDIQUES = [
  'SAS',
  'SARL',
  'SA',
  'EURL',
  'EI',
  'SASU',
  'SNC',
  'Auto-entrepreneur',
  'Association',
  'Autre',
];

const TabPresentation: React.FC = () => {
  const { profile, updatePresentation, updateEntreprise, updateLots, updateLotsForLotTechnique, isSaving } = useProfile();
  const profilType = useProfilType(); // FRONT-09: Pour conditionner l'affichage des lots

  const [isEditing, setIsEditing] = useState(false);

  // Autocomplétion adresse IGN
  const [adresseInput, setAdresseInput] = useState('');
  const [adresseSuggestions, setAdresseSuggestions] = useState<IgnSuggestion[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const adresseUserTypingRef = useRef(false); // Flag : autocomplete uniquement si l'utilisateur tape

  // FRONT-24: Données entreprise pour l'édition
  const [entrepriseFormData, setEntrepriseFormData] = useState<EntrepriseFormData>({
    precision: '',
    RCS: '',
    statut_juridique: '',
    capital: null,
    date_creation: null,
    redressement_judiciaire: false,
    adresse: {
      numero_voie: '',
      type_voie: '',
      nom_voie: '',
      code_postal: '',
      ville: '',
      pays: 'France',
    },
  });

  const [formData, setFormData] = useState<PresentationData>({
    presentation_entreprise: '',
    presentation_operationnel: '',
    presentation_humain: '',
    presentation_environnement: '',
    ca_n1: null,
    part_ca_n1: null,
    ca_n2: null,
    part_ca_n2: null,
    ca_n3: null,
    part_ca_n3: null,
    rh_n1: null,
    part_rh_n1: null,
    rh_n2: null,
    part_rh_n2: null,
    rh_n3: null,
    part_rh_n3: null,
  });

  // Années pour CA et effectifs (3 dernières années)
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear - 2, currentYear - 3];

  // Charger les données du profil (uniquement hors mode édition pour ne pas écraser les saisies en cours)
  useEffect(() => {
    if (isEditing) return;
    if (profile?.presentation) {
      const p = profile.presentation;
      setFormData({
        presentation_entreprise: p.presentation_entreprise || '',
        presentation_operationnel: p.presentation_operationnel || '',
        presentation_humain: p.presentation_humain || '',
        presentation_environnement: p.presentation_environnement || '',
        ca_n1: p.ca_n1,
        part_ca_n1: p.part_ca_n1,
        ca_n2: p.ca_n2,
        part_ca_n2: p.part_ca_n2,
        ca_n3: p.ca_n3,
        part_ca_n3: p.part_ca_n3,
        rh_n1: p.rh_n1,
        part_rh_n1: p.part_rh_n1,
        rh_n2: p.rh_n2,
        part_rh_n2: p.part_rh_n2,
        rh_n3: p.rh_n3,
        part_rh_n3: p.part_rh_n3,
      });
    }

    // FRONT-24: Charger les données entreprise
    if (profile?.entreprise) {
      const e = profile.entreprise;
      setEntrepriseFormData({
        precision: e.precision || '',
        RCS: e.RCS || '',
        statut_juridique: e.statut_juridique || '',
        capital: e.capital,
        date_creation: e.date_creation,
        redressement_judiciaire: e.redressement_judiciaire || false,
        adresse: {
          numero_voie: e.adresse?.numero_voie || '',
          type_voie: e.adresse?.type_voie || '',
          nom_voie: e.adresse?.nom_voie || '',
          code_postal: e.adresse?.code_postal || '',
          ville: e.adresse?.ville || '',
          pays: e.adresse?.pays || 'France',
        },
      });
    }

    // Initialiser l'input adresse avec l'adresse existante
    if (profile?.entreprise?.adresse) {
      const a = profile.entreprise.adresse;
      const adresseComplete = [a.nom_voie, a.code_postal, a.ville].filter(Boolean).join(', ');
      adresseUserTypingRef.current = false; // Reset : pas d'autocomplete au chargement
      setAdresseInput(adresseComplete);
    }
  }, [profile, isEditing]);

  // Autocomplétion adresse IGN
  useEffect(() => {
    // Autocomplete uniquement si l'utilisateur tape activement dans le champ
    if (!adresseUserTypingRef.current) {
      return;
    }

    if (adresseInput.trim().length < 3) {
      setAdresseSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();

      fetch(`https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(adresseInput)}&index=StreetAddress&limit=8`,
        { signal: controller.signal })
        .then(r => r.ok ? r.json() : Promise.reject(r))
        .then(json => {
          if (json && json.results) {
            const suggestions = json.results.map((result: any) => ({
              label: result.fulltext,
              houseNumber: result.kind === 'housenumber' ? result.fulltext.split(' ')[0] : '',
              street: result.street,
              postcode: result.zipcode,
              city: result.city
            }));
            setAdresseSuggestions(suggestions);
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Erreur lors de la récupération des adresses:', error);
          }
        });

      return () => controller.abort();
    }, 300);
  }, [adresseInput]);

  // Sélection d'une adresse depuis les suggestions IGN
  const handleSelectAdresse = (suggestion: IgnSuggestion) => {
    adresseUserTypingRef.current = false;
    setEntrepriseFormData(prev => ({
      ...prev,
      adresse: {
        ...prev.adresse,
        nom_voie: suggestion.street || suggestion.label,
        code_postal: suggestion.postcode || '',
        ville: suggestion.city || '',
      },
    }));
    setAdresseInput(suggestion.label);
    setAdresseSuggestions([]);
  };

  // Gestion des changements de champs texte
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Gestion des champs numériques
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value ? parseFloat(value) : null,
    }));
  };

  // Soumission du formulaire (présentation + entreprise)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // FRONT-24: Sauvegarder entreprise ET présentation
    const [entrepriseSuccess, presentationSuccess] = await Promise.all([
      updateEntreprise(entrepriseFormData),
      updatePresentation(formData),
    ]);

    if (entrepriseSuccess && presentationSuccess) {
      setIsEditing(false);
    }
  };

  // Annuler les modifications (présentation + entreprise)
  const handleCancel = () => {
    // Réinitialiser présentation
    if (profile?.presentation) {
      const p = profile.presentation;
      setFormData({
        presentation_entreprise: p.presentation_entreprise || '',
        presentation_operationnel: p.presentation_operationnel || '',
        presentation_humain: p.presentation_humain || '',
        presentation_environnement: p.presentation_environnement || '',
        ca_n1: p.ca_n1,
        part_ca_n1: p.part_ca_n1,
        ca_n2: p.ca_n2,
        part_ca_n2: p.part_ca_n2,
        ca_n3: p.ca_n3,
        part_ca_n3: p.part_ca_n3,
        rh_n1: p.rh_n1,
        part_rh_n1: p.part_rh_n1,
        rh_n2: p.rh_n2,
        part_rh_n2: p.part_rh_n2,
        rh_n3: p.rh_n3,
        part_rh_n3: p.part_rh_n3,
      });
    }
    // FRONT-24: Réinitialiser entreprise
    if (profile?.entreprise) {
      const e = profile.entreprise;
      setEntrepriseFormData({
        precision: e.precision || '',
        RCS: e.RCS || '',
        statut_juridique: e.statut_juridique || '',
        capital: e.capital,
        date_creation: e.date_creation,
        redressement_judiciaire: e.redressement_judiciaire || false,
        adresse: {
          numero_voie: e.adresse?.numero_voie || '',
          type_voie: e.adresse?.type_voie || '',
          nom_voie: e.adresse?.nom_voie || '',
          code_postal: e.adresse?.code_postal || '',
          ville: e.adresse?.ville || '',
          pays: e.adresse?.pays || 'France',
        },
      });
    }
    setIsEditing(false);
  };

  // ============================================
  // FRONT-24: Gestion des informations légales
  // ============================================

  // Formater le SIRET avec des espaces
  const formatSiret = (siret?: string | null): string => {
    if (!siret) return '-';
    // Format: 123 456 789 01234
    return siret.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4');
  };

  // Formater l'adresse pour l'affichage
  const formatAdresse = (adresse?: AdresseData | null): string => {
    if (!adresse) return '-';
    const ligne1 = [adresse.numero_voie, adresse.type_voie, adresse.nom_voie]
      .filter(Boolean)
      .join(' ');
    const ligne2 = `${adresse.code_postal || ''} ${adresse.ville || ''}`.trim();
    const ligne3 = adresse.pays;
    return [ligne1, ligne2, ligne3].filter(Boolean).join('\n');
  };

  // Gestion des changements des champs entreprise
  const handleEntrepriseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (name.startsWith('adresse.')) {
      const adresseField = name.replace('adresse.', '');
      setEntrepriseFormData(prev => ({
        ...prev,
        adresse: {
          ...prev.adresse,
          [adresseField]: value,
        },
      }));
    } else if (type === 'checkbox') {
      setEntrepriseFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else if (name === 'capital' || name === 'date_creation') {
      setEntrepriseFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : null,
      }));
    } else {
      setEntrepriseFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Les handlers entreprise ont été fusionnés dans handleSubmit et handleCancel

  // Formatage montant
  const formatMontant = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '-';
    return `${val.toLocaleString('fr-FR')} €`;
  };

  // Formatage effectif
  const formatEffectif = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '-';
    return `${val} salarié${val > 1 ? 's' : ''}`;
  };

  // Formatage pourcentage
  const formatPart = (val: number | null | undefined): string => {
    if (val === null || val === undefined) return '';
    return `(${val}% du CA total)`;
  };

  // Mode lecture
  if (!isEditing) {
    return (
      <div className="tab-presentation">
        <div className="tab-header">
          <h2>Présentation de l'entreprise</h2>
          <Button
            value="Modifier"
            variant="secondary"
            onClick={() => setIsEditing(true)}
          />
        </div>

        <div className="presentation-content">
          {/* FRONT-24: Section Informations légales (lecture seule) */}
          <section className="form-section-block">
            <div className="section-banner">Informations légales</div>
            <div className="section-content">
              <div className="view-row three-cols">
                <div className="view-group readonly">
                  <label>Nom de l'entreprise 🔒</label>
                  <p className="value-text">{profile?.entreprise?.nom || '-'}</p>
                </div>
                <div className="view-group">
                  <label>Précision</label>
                  <p className="value-text">{profile?.entreprise?.precision || '-'}</p>
                </div>
                <div className="view-group readonly">
                  <label>SIRET 🔒</label>
                  <p className="value-text">{formatSiret(profile?.entreprise?.siret)}</p>
                </div>
              </div>

              <div className="view-row three-cols">
                <div className="view-group">
                  <label>RCS</label>
                  <p className="value-text">{profile?.entreprise?.RCS || '-'}</p>
                </div>
                <div className="view-group">
                  <label>Statut juridique</label>
                  <p className="value-text">{profile?.entreprise?.statut_juridique || '-'}</p>
                </div>
                <div className="view-group">
                  <label>Capital</label>
                  <p className="value-text">
                    {profile?.entreprise?.capital
                      ? `${profile.entreprise.capital.toLocaleString('fr-FR')} €`
                      : '-'}
                  </p>
                </div>
              </div>

              <div className="view-row three-cols">
                <div className="view-group">
                  <label>Année de création</label>
                  <p className="value-text">{profile?.entreprise?.date_creation || '-'}</p>
                </div>
                <div className="view-group">
                  <label>Redressement judiciaire</label>
                  <p className="value-text">
                    {profile?.entreprise?.redressement_judiciaire ? 'Oui' : 'Non'}
                  </p>
                </div>
                <div className="view-group">
                  <label>Adresse</label>
                  <p className="value-text adresse-display">
                    {formatAdresse(profile?.entreprise?.adresse)}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 1: Description de l'entreprise */}
          <section className="form-section-block">
            <div className="section-banner">Description de l'entreprise</div>
            <div className="section-content">
              <div className="view-group">
                <label>Présentation de l'entreprise</label>
                <span className="char-limit-hint">2000 char max</span>
                <p className="description-text">
                  {profile?.presentation?.presentation_entreprise || 'Non renseignée'}
                </p>
              </div>

              <div className="view-group">
                <label>Présentation des moyens humains et matériel utilisés en général pour vos opérations</label>
                <span className="char-limit-hint">3000 char max</span>
                <p className="description-text">
                  {profile?.presentation?.presentation_humain || 'Non renseignée'}
                </p>
              </div>

              <div className="view-group">
                <label>Présentation des modes opératoires utilisés en général pour vos opérations</label>
                <span className="char-limit-hint">2000 char max</span>
                <p className="description-text">
                  {profile?.presentation?.presentation_operationnel || 'Non renseignée'}
                </p>
              </div>

              <div className="view-group">
                <label>Engagements environnementaux et sociaux</label>
                <span className="char-limit-hint">2000 char max</span>
                <p className="description-text">
                  {profile?.presentation?.presentation_environnement || 'Non renseignée'}
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: Capacités économiques et financières */}
          <section className="form-section-block">
            <div className="section-banner">Capacités économiques et financières</div>
            <div className="section-content">
              <p className="section-description">
                Chiffre d'affaires global annuel en € et Part du CA des prestations similaires (%)
              </p>
              <div className="capacites-grid ca-grid view-mode">
                <div className="view-item">
                  <label>(N-1)</label>
                  <span>{profile?.presentation?.ca_n1?.toLocaleString('fr-FR') || '-'} €</span>
                </div>
                <div className="view-item">
                  <label>Part du CA (N-1)</label>
                  <span>{profile?.presentation?.part_ca_n1 ?? '-'} %</span>
                </div>
                <div className="view-item">
                  <label>(N-2)</label>
                  <span>{profile?.presentation?.ca_n2?.toLocaleString('fr-FR') || '-'} €</span>
                </div>
                <div className="view-item">
                  <label>Part du CA (N-2)</label>
                  <span>{profile?.presentation?.part_ca_n2 ?? '-'} %</span>
                </div>
                <div className="view-item">
                  <label>(N-3)</label>
                  <span>{profile?.presentation?.ca_n3?.toLocaleString('fr-FR') || '-'} €</span>
                </div>
                <div className="view-item">
                  <label>Part du CA (N-3)</label>
                  <span>{profile?.presentation?.part_ca_n3 ?? '-'} %</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Capacités techniques et professionnelles */}
          <section className="form-section-block">
            <div className="section-banner">Capacités techniques et professionnelles</div>
            <div className="section-content">
              <p className="section-description">
                Effectifs moyens annuels et Part du personnel d'encadrement (%)
              </p>
              <div className="capacites-grid rh-grid view-mode">
                <div className="view-item">
                  <label>(N-1)</label>
                  <span>{profile?.presentation?.rh_n1 ?? '-'} ETP</span>
                </div>
                <div className="view-item">
                  <label>Part (N-1)</label>
                  <span>{profile?.presentation?.part_rh_n1 ?? '-'} %</span>
                </div>
                <div className="view-item">
                  <label>(N-2)</label>
                  <span>{profile?.presentation?.rh_n2 ?? '-'} ETP</span>
                </div>
                <div className="view-item">
                  <label>Part (N-2)</label>
                  <span>{profile?.presentation?.part_rh_n2 ?? '-'} %</span>
                </div>
                <div className="view-item">
                  <label>(N-3)</label>
                  <span>{profile?.presentation?.rh_n3 ?? '-'} ETP</span>
                </div>
                <div className="view-item">
                  <label>Part (N-3)</label>
                  <span>{profile?.presentation?.part_rh_n3 ?? '-'} %</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Lots prestataire libre (non-verrouillés) */}
          {profilType.showLots && (
            <section className="form-section-block lots-section">
              <div className="section-banner">Lots techniques et géographiques en tant que prestataire libre</div>
              <div className="section-content">
                <LotsViewByLotTechnique
                  key={(profile?.lots?.lots_departements || []).filter(ld => !ld.is_locked).map(ld => `${ld.lot_technique_id}-${ld.departement_id}`).join(',') || 'libre-empty'}
                  lotsDepartements={(profile?.lots?.lots_departements || []).filter(ld => !ld.is_locked)}
                  lotsTechniques={(profile?.lots?.lots_techniques || []).filter(lt =>
                    (profile?.lots?.lots_departements || []).some(ld => !ld.is_locked && ld.lot_technique_id === lt.id)
                  )}
                  emptyMessage="Vous n'êtes référencé sur aucun lot en tant que prestataire libre"
                />
              </div>
            </section>
          )}

          {/* Section 5: Lots prestataire titulaire (verrouillés, lecture seule) */}
          {profilType.showLots && (
            <section className="form-section-block lots-section">
              <div className="section-banner">Lots techniques et géographiques en tant que prestataire titulaire</div>
              <div className="section-content">
                <LotsViewByLotTechnique
                  key={(profile?.lots?.lots_departements || []).filter(ld => ld.is_locked).map(ld => `${ld.lot_technique_id}-${ld.departement_id}`).join(',') || 'titulaire-empty'}
                  lotsDepartements={(profile?.lots?.lots_departements || []).filter(ld => ld.is_locked)}
                  lotsTechniques={(profile?.lots?.lots_techniques || []).filter(lt =>
                    (profile?.lots?.lots_departements || []).some(ld => ld.is_locked && ld.lot_technique_id === lt.id)
                  )}
                  emptyMessage="Vous n'êtes titulaire sur aucun lot"
                />
              </div>
            </section>
          )}
        </div>
      </div>
    );
  }

  // Mode édition
  return (
    <div className="tab-presentation">
      <div className="tab-header">
        <h2>Modifier la présentation</h2>
      </div>

      <form onSubmit={handleSubmit} className="presentation-form">
        {/* FRONT-24: Section Informations légales (édition) */}
        <section className="form-section-block">
          <div className="section-banner">Informations légales</div>
          <div className="section-content">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-entreprise-nom">Nom de l'entreprise 🔒</label>
                <input
                  type="text"
                  id="edit-entreprise-nom"
                  value={profile?.entreprise?.nom || ''}
                  disabled
                  className="input-disabled"
                  title="Le nom de l'entreprise ne peut pas être modifié"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-precision">Précision</label>
                <input
                  type="text"
                  id="edit-precision"
                  name="precision"
                  value={entrepriseFormData.precision || ''}
                  onChange={handleEntrepriseChange}
                  placeholder="Ex: (Agence Alpes Côte d'Azur)"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-entreprise-siret">SIRET 🔒</label>
                <input
                  type="text"
                  id="edit-entreprise-siret"
                  value={formatSiret(profile?.entreprise?.siret)}
                  disabled
                  className="input-disabled"
                  title="Le SIRET ne peut pas être modifié"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-RCS">RCS</label>
                <input
                  type="text"
                  id="edit-RCS"
                  name="RCS"
                  value={entrepriseFormData.RCS || ''}
                  onChange={handleEntrepriseChange}
                  placeholder="Ex: Paris B 123 456 789"
                />
              </div>
            </div>

            <div className="form-row three-cols">
              <div className="form-group">
                <label htmlFor="edit-statut_juridique">Statut juridique</label>
                <select
                  id="edit-statut_juridique"
                  name="statut_juridique"
                  value={entrepriseFormData.statut_juridique || ''}
                  onChange={handleEntrepriseChange}
                >
                  <option value="">Sélectionner...</option>
                  {FORMES_JURIDIQUES.map(forme => (
                    <option key={forme} value={forme}>{forme}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="edit-capital">Capital (€)</label>
                <input
                  type="number"
                  id="edit-capital"
                  name="capital"
                  value={entrepriseFormData.capital ?? ''}
                  onChange={handleEntrepriseChange}
                  min="0"
                  placeholder="10000"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-date_creation">Année de création</label>
                <input
                  type="number"
                  id="edit-date_creation"
                  name="date_creation"
                  value={entrepriseFormData.date_creation ?? ''}
                  onChange={handleEntrepriseChange}
                  min="1800"
                  max={currentYear}
                  placeholder="2015"
                />
              </div>
            </div>

            {/* Redressement judiciaire avec Switch comme Register.tsx */}
            <div className="form-group">
              <label htmlFor="edit-redressement-switch" className="redressement-label">
                Redressement judiciaire
              </label>
              <div className="redressement-label-block redressement-section">
                <div className="redressement-description">
                  L'entreprise est-elle en redressement ?
                </div>
                <div className="redressement-switch-control">
                  <Switch
                    id="edit-redressement-switch"
                    isOn={entrepriseFormData.redressement_judiciaire || false}
                    handleToggle={(e: React.ChangeEvent<HTMLInputElement>) => setEntrepriseFormData(prev => ({ ...prev, redressement_judiciaire: e.target.checked }))}
                    onColor="#da7027"
                  />
                </div>
              </div>

            </div>

            <div className="form-row">
              <div className="form-group adresse-autocomplete-container" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="edit-adresse-search">Adresse*</label>
                <input
                  type="text"
                  id="edit-adresse-search"
                  name="adresse_search"
                  value={adresseInput}
                  onChange={(e) => {
                    adresseUserTypingRef.current = true; // L'utilisateur tape → activer l'autocomplete
                    setAdresseInput(e.target.value);
                    // Mettre à jour le champ adresse manuellement
                    setEntrepriseFormData(prev => ({
                      ...prev,
                      adresse: { ...prev.adresse, nom_voie: e.target.value },
                    }));
                  }}
                  placeholder="Commencez à taper une adresse..."
                  autoComplete="off"
                  required
                />
                {adresseSuggestions.length > 0 && (
                  <ul className="autocomplete-list adresse-suggestions">
                    {adresseSuggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectAdresse(suggestion);
                        }}
                        className="adresse-suggestion-item"
                      >
                        {suggestion.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="form-row three-cols">
              <div className="form-group">
                <label htmlFor="edit-adresse-code_postal">Code postal*</label>
                <input
                  type="text"
                  id="edit-adresse-code_postal"
                  name="adresse.code_postal"
                  value={entrepriseFormData.adresse?.code_postal || ''}
                  onChange={handleEntrepriseChange}
                  pattern="[0-9]{5}"
                  maxLength={5}
                  placeholder="75001"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-adresse-ville">Ville*</label>
                <input
                  type="text"
                  id="edit-adresse-ville"
                  name="adresse.ville"
                  value={entrepriseFormData.adresse?.ville || ''}
                  onChange={handleEntrepriseChange}
                  placeholder="Paris"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-adresse-pays">Pays*</label>
                <input
                  type="text"
                  id="edit-adresse-pays"
                  name="adresse.pays"
                  value={entrepriseFormData.adresse?.pays || 'France'}
                  onChange={handleEntrepriseChange}
                  required
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 1: Description de l'entreprise */}
        <section className="form-section-block">
          <div className="section-banner">Description de l'entreprise</div>
          <div className="section-content">
            <div className="form-group full-width">
              <label htmlFor="presentation_entreprise">Présentation de l'entreprise</label>
              <textarea
                id="presentation_entreprise"
                name="presentation_entreprise"
                value={formData.presentation_entreprise || ''}
                onChange={handleChange}
                rows={4}
                maxLength={2000}
                placeholder="Décrivez votre entreprise, son historique, ses valeurs..."
              />
              <span className="char-counter">{(formData.presentation_entreprise || '').length}/2000</span>
            </div>

            <div className="form-group full-width">
              <label htmlFor="presentation_humain">Présentation des moyens humains et matériel utilisés en général pour vos opérations</label>
              <textarea
                id="presentation_humain"
                name="presentation_humain"
                value={formData.presentation_humain || ''}
                onChange={handleChange}
                rows={3}
                maxLength={3000}
                placeholder="Décrivez vos ressources humaines, équipements, matériels..."
              />
              <span className="char-counter">{(formData.presentation_humain || '').length}/3000</span>
            </div>

            <div className="form-group full-width">
              <label htmlFor="presentation_operationnel">Présentation des modes opératoires utilisés en général pour vos opérations</label>
              <textarea
                id="presentation_operationnel"
                name="presentation_operationnel"
                value={formData.presentation_operationnel || ''}
                onChange={handleChange}
                rows={3}
                maxLength={2000}
                placeholder="Décrivez vos méthodes de travail, processus, procédures..."
              />
              <span className="char-counter">{(formData.presentation_operationnel || '').length}/2000</span>
            </div>

            <div className="form-group full-width">
              <label htmlFor="presentation_environnement">Engagements environnementaux et sociaux</label>
              <textarea
                id="presentation_environnement"
                name="presentation_environnement"
                value={formData.presentation_environnement || ''}
                onChange={handleChange}
                rows={3}
                maxLength={2000}
                placeholder="Décrivez vos engagements RSE, certifications environnementales..."
              />
              <span className="char-counter">{(formData.presentation_environnement || '').length}/2000</span>
            </div>
          </div>
        </section>

        {/* Section 2: Capacités économiques et financières */}
        <section className="form-section-block">
          <div className="section-banner">Capacités économiques et financières</div>
          <div className="section-content">
            <p className="section-description">
              Chiffre d'affaires global annuel en € et Part du CA des prestations similaires (%)
            </p>
            <div className="capacites-grid ca-grid">
              <div className="form-group">
                <label>(N-1)</label>
                <input
                  type="number"
                  id="ca_n1"
                  name="ca_n1"
                  value={formData.ca_n1 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  placeholder="€"
                />
              </div>
              <div className="form-group">
                <label>Part du CA (N-1)</label>
                <input
                  type="number"
                  id="part_ca_n1"
                  name="part_ca_n1"
                  value={formData.part_ca_n1 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  max="100"
                  placeholder="%"
                />
              </div>
              <div className="form-group">
                <label>(N-2)</label>
                <input
                  type="number"
                  id="ca_n2"
                  name="ca_n2"
                  value={formData.ca_n2 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  placeholder="€"
                />
              </div>
              <div className="form-group">
                <label>Part du CA (N-2)</label>
                <input
                  type="number"
                  id="part_ca_n2"
                  name="part_ca_n2"
                  value={formData.part_ca_n2 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  max="100"
                  placeholder="%"
                />
              </div>
              <div className="form-group">
                <label>(N-3)</label>
                <input
                  type="number"
                  id="ca_n3"
                  name="ca_n3"
                  value={formData.ca_n3 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  placeholder="€"
                />
              </div>
              <div className="form-group">
                <label>Part du CA (N-3)</label>
                <input
                  type="number"
                  id="part_ca_n3"
                  name="part_ca_n3"
                  value={formData.part_ca_n3 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  max="100"
                  placeholder="%"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Capacités techniques et professionnelles */}
        <section className="form-section-block">
          <div className="section-banner">Capacités techniques et professionnelles</div>
          <div className="section-content">
            <p className="section-description">
              Effectifs moyens annuels et Part du personnel d'encadrement (%)
            </p>
            <div className="capacites-grid rh-grid">
              <div className="form-group">
                <label>(N-1)</label>
                <input
                  type="number"
                  id="rh_n1"
                  name="rh_n1"
                  value={formData.rh_n1 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  placeholder="ETP"
                />
              </div>
              <div className="form-group">
                <label>Part (N-1)</label>
                <input
                  type="number"
                  id="part_rh_n1"
                  name="part_rh_n1"
                  value={formData.part_rh_n1 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  max="100"
                  placeholder="%"
                />
              </div>
              <div className="form-group">
                <label>(N-2)</label>
                <input
                  type="number"
                  id="rh_n2"
                  name="rh_n2"
                  value={formData.rh_n2 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  placeholder="ETP"
                />
              </div>
              <div className="form-group">
                <label>Part (N-2)</label>
                <input
                  type="number"
                  id="part_rh_n2"
                  name="part_rh_n2"
                  value={formData.part_rh_n2 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  max="100"
                  placeholder="%"
                />
              </div>
              <div className="form-group">
                <label>(N-3)</label>
                <input
                  type="number"
                  id="rh_n3"
                  name="rh_n3"
                  value={formData.rh_n3 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  placeholder="ETP"
                />
              </div>
              <div className="form-group">
                <label>Part (N-3)</label>
                <input
                  type="number"
                  id="part_rh_n3"
                  name="part_rh_n3"
                  value={formData.part_rh_n3 ?? ''}
                  onChange={handleNumberChange}
                  min="0"
                  max="100"
                  placeholder="%"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Lots prestataire libre (édition) */}
        {/* FRONT-09: PL et AC peuvent modifier, lots AC titulaires sont verrouillés */}
        {profilType.showLots && (
          <section className="form-section-block lots-section">
            <div className="section-banner">Lots techniques et géographiques en tant que prestataire libre</div>
            <div className="section-content">
              <LotsSelectorV2
                lotsDepartements={(profile?.lots?.lots_departements || [])
                  .filter(ld => !ld.is_locked)
                  .map(ld => ({
                    lot_technique_id: ld.lot_technique_id,
                    lot_technique_code: ld.lot_technique_code,
                    lot_technique_nom: ld.lot_technique_nom,
                    departement_id: ld.departement_id,
                    departement_nom: ld.departement_nom,
                  }))}
                onLotsChange={updateLotsForLotTechnique}
                disabled={false}
                // FRONT-09: Exclure les lots titulaires de la sélection possible
                lotsAccordCadre={(profile?.lots?.lots_departements || [])
                  .filter(ld => ld.is_locked)
                  .map(ld => ({
                    lot_technique_id: ld.lot_technique_id,
                    departement_id: ld.departement_id,
                    rang: ld.rang,
                  }))}
              />
            </div>
          </section>
        )}

        {/* Section 5: Lots prestataire titulaire (lecture seule, non modifiables) */}
        {profilType.showLots && (
          <section className="form-section-block lots-section">
            <div className="section-banner">Lots techniques et géographiques en tant que prestataire titulaire</div>
            <div className="section-content">
              <LotsViewByLotTechnique
                key={(profile?.lots?.lots_departements || []).filter(ld => ld.is_locked).map(ld => `${ld.lot_technique_id}-${ld.departement_id}`).join(',') || 'titulaire-edit-empty'}
                lotsDepartements={(profile?.lots?.lots_departements || []).filter(ld => ld.is_locked)}
                lotsTechniques={(profile?.lots?.lots_techniques || []).filter(lt =>
                  (profile?.lots?.lots_departements || []).some(ld => ld.is_locked && ld.lot_technique_id === lt.id)
                )}
                emptyMessage="Vous n'êtes titulaire sur aucun lot"
              />
            </div>
          </section>
        )}

        {/* Actions */}
        <div className="form-actions">
          <Button
            value="Annuler"
            variant="secondary"
            onClick={handleCancel}
            disabled={isSaving}
          />
          <Button
            value={isSaving ? "Enregistrement..." : "Enregistrer"}
            variant="primary"
            type="submit"
            disabled={isSaving}
          />
        </div>
      </form>
    </div>
  );
};

export default TabPresentation;
